import { Box, Button, Flex, HStack, Heading, Image, Text, Tooltip, useBoolean } from '@chakra-ui/react';
import bgBrandModelImage from 'assets/background/auto_brand.png';
import { ReactComponent as MagicPenIcon } from 'assets/icons/magic_pen.svg';
import { AUTO_BRAND_OPENED, Mixpanel } from 'libs/third-party/Mixpanel';
import { useAppSelector } from 'modules/Editor/store';
import { selectBrandIdx } from 'modules/Editor/store/editorSettingsSelector';
import { useBrand } from 'modules/User';
import { memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { customStyles } from 'theme/custom';

import { getBrandColorPalette, serializeLogoData } from '../WidgetMenu/SideMenu/BrandKitMenu/BrandKitMenu.helper';
import { AutoBrand } from './AutoBrand';

interface AutoBrandButtonProps {
  toggle: {
    on: () => void;
    off: () => void;
    toggle: () => void;
  };
  isOpen: boolean;
  openAutoBrand: () => void;
}

/**
 * different style to gate the auto brand
 * @param AutoBrandButtonProps
 * @returns
 */
const AutoBrandButton = ({ isOpen, toggle, openAutoBrand }: AutoBrandButtonProps) => {
  const { t } = useTranslation('auto_brand', { useSuspense: false, keyPrefix: 'modals.autoBrand' });
  return (
    <>
      <Button
        onClick={openAutoBrand}
        data-testid='brand-button'
        bgColor='violet.50'
        textColor='violet.700'
        justifyContent='left'
        leftIcon={
          <Box bgColor='white' borderRadius='md' p='1.5' h='8'>
            <MagicPenIcon fill='var(--vg-colors-violet-700)' />
          </Box>
        }
        w='100%'
        fontSize='xs'
        fontWeight='medium'
        p='1.5'
        h='100%'
        marginY='2'
        _active={{
          background: 'violet.100',
        }}
        _hover={{
          background: 'violet.100',
        }}
      >
        {t('autoFill')}
      </Button>
      <AutoBrand isOpen={isOpen} onClose={toggle.off} />
    </>
  );
};

type CardType = 'card' | 'button';

export interface AutoBrandCardProps {
  handleTabsChange?: () => void;
  type?: CardType;
  isModalOpened?: (status: boolean) => void;
  mixpanelFrom: string;
  canEditBrandKit?: boolean;
}
export const AutoBrandCard = memo(function AutoBrandCard({
  handleTabsChange,
  type = 'card',
  isModalOpened = () => {},
  mixpanelFrom,
  canEditBrandKit = true,
}: AutoBrandCardProps) {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });
  const [open, toggle] = useBoolean(false);

  const brandIdx = useAppSelector(selectBrandIdx);
  const { data: brandData } = useBrand();

  const brandColorPalettes = useMemo(() => {
    return brandData && brandData.length > 0 ? getBrandColorPalette(brandData[brandIdx]) : [];
  }, [brandIdx, brandData]);

  const hasNoColorPalettes = brandData && brandColorPalettes.length > 0;
  const brandLogos = brandData?.[brandIdx]?.logos ?? [];
  const serializedLogoData = serializeLogoData({ data: brandLogos });
  const showLogos = !!serializedLogoData.length;
  const hasNoBrand = !hasNoColorPalettes && !showLogos;

  /**
   * It handles MP events and open the auto brand modal
   */
  const openAutoBrand = () => {
    if (!canEditBrandKit) return;
    Mixpanel.track(AUTO_BRAND_OPENED, { from: mixpanelFrom });
    toggle.on();
  };

  /**
   * this useEffect pass the modal open state to the callback isModalOpened
   */
  useEffect(() => {
    isModalOpened(open);
  }, [isModalOpened, open]);

  // no renders when we don't have a brand
  if (!hasNoBrand) return null;

  if (type === 'button') return <AutoBrandButton isOpen={open} toggle={toggle} openAutoBrand={openAutoBrand} />;

  return (
    <Flex
      borderRadius='sm'
      borderLeft={customStyles.container.violetGradient.borderLeft}
      background={customStyles.container.violetGradient.background}
    >
      <Tooltip
        hasArrow
        placement='right'
        borderRadius='base'
        gutter={12}
        label={
          <Flex flexDir='column' py={1}>
            <Text>{t('onlyAdmin')}</Text> <Text>{t('contactAdmin')}</Text>
          </Flex>
        }
        isDisabled={canEditBrandKit}
      >
        <Box
          onClick={openAutoBrand}
          as='button'
          p='4'
          flexDir='column'
          justifyContent='space-around'
          _hover={{ backgroundColor: 'var(--vg-colors-a11yMenu-hover)' }}
          data-testid='auto-brand-card'
          boxShadow='sm'
        >
          <HStack gap='2'>
            <Image src={bgBrandModelImage} w='65px' minH='54px' role='presentation' />
            <Flex flexDirection='column' textAlign='left'>
              <Heading fontSize={'sm'} marginBottom='2'>
                {t('autoBrand')}
              </Heading>
              <Text fontSize={'xs'}>{t('setupYourBrand')}</Text>
            </Flex>
          </HStack>
        </Box>
      </Tooltip>
      <AutoBrand isOpen={open} onClose={toggle.off} handleTabsChange={handleTabsChange} />
    </Flex>
  );
});
